import { z } from 'zod';

import { KnowledgeSlugSchema } from './folder';

// `internal` articles are team-only; `customer` articles are safe to surface to
// end users. The retrieval scope (customer vs all) is derived from this.
export const KNOWLEDGE_VISIBILITIES = ['internal', 'customer'] as const;
export const KnowledgeVisibilitySchema = z.enum(KNOWLEDGE_VISIBILITIES);
export type KnowledgeVisibility = z.infer<typeof KnowledgeVisibilitySchema>;

// Authoring lifecycle. Agents see only `approved`; editing an approved article
// reverts it to `draft` (see the article service).
export const KNOWLEDGE_STATUSES = ['draft', 'approved', 'archived'] as const;
export const KnowledgeStatusSchema = z.enum(KNOWLEDGE_STATUSES);
export type KnowledgeStatus = z.infer<typeof KnowledgeStatusSchema>;

export const KnowledgeArticleSchema = z.object({
  id: z.uuid(),
  folderId: z.uuid(),
  slug: KnowledgeSlugSchema,
  title: z.string(),
  // Customer questions this article answers, phrased the way customers ask —
  // the primary index signal.
  questions: z.array(z.string()),
  // Keywords + synonyms + common misspellings.
  tags: z.array(z.string()),
  visibility: KnowledgeVisibilitySchema,
  status: KnowledgeStatusSchema,
  // Content revision: bumped on every edit, untouched by approve/archive. An MCP
  // writer names the version it read so a stale edit or approval fails instead of
  // publishing content nobody reviewed.
  version: z.number().int().positive(),
  body: z.string(),
  ownerId: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type KnowledgeArticle = z.infer<typeof KnowledgeArticleSchema>;

// The owner's email, joined for display so the admin UI shows a name rather than a
// bare uuid. Read-only — ownership is set via `ownerId`.
export const KnowledgeArticleDetailSchema = KnowledgeArticleSchema.extend({
  ownerEmail: z.email(),
});
export type KnowledgeArticleDetail = z.infer<typeof KnowledgeArticleDetailSchema>;

// One row in the browse view's per-folder article list — omits the (potentially
// large) body, carries the display fields.
export const KnowledgeArticleSummarySchema = KnowledgeArticleSchema.omit({
  body: true,
}).extend({
  ownerEmail: z.email(),
});
export type KnowledgeArticleSummary = z.infer<typeof KnowledgeArticleSummarySchema>;

// status is not settable through create/update — it moves only through the
// approve/archive/unarchive actions (see the article service).
export const CreateKnowledgeArticleSchema = z.object({
  folderId: z.uuid(),
  slug: KnowledgeSlugSchema,
  title: z.string().min(1),
  questions: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),
  visibility: KnowledgeVisibilitySchema,
  body: z.string().default(''),
  ownerId: z.uuid(),
});
export type CreateKnowledgeArticle = z.infer<typeof CreateKnowledgeArticleSchema>;

// Every field optional; editing an approved article reverts it to draft server-side.
export const UpdateKnowledgeArticleSchema = z.object({
  folderId: z.uuid().optional(),
  slug: KnowledgeSlugSchema.optional(),
  title: z.string().min(1).optional(),
  questions: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  visibility: KnowledgeVisibilitySchema.optional(),
  body: z.string().optional(),
  ownerId: z.uuid().optional(),
});
export type UpdateKnowledgeArticle = z.infer<typeof UpdateKnowledgeArticleSchema>;
